import React, { useState } from 'react';
import devToolsService from '../../utils/services/devToolsService';
import snipeLogo from '../../assets/snipe_logo.png';

function PasswordProtection({ onAccessGranted }) {
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!password.trim()) {
            setError('Please enter a password');
            return;
        }

        setIsLoading(true);
        setError('');

        const result = await devToolsService.verifyWebsitePassword(password);

        setIsLoading(false);

        if (result.success && result.accessGranted) {
            // Store access in sessionStorage (cleared when browser closes)
            sessionStorage.setItem('dev_access_granted', 'true');
            onAccessGranted();
        } else {
            setError(result.message || 'Incorrect password');
            setPassword('');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#020617] via-[#0a0f1e] to-black flex items-center justify-center p-4">
            {/* Background decorative elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/5 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-500/5 rounded-full blur-3xl"></div>
            </div>

            {/* Password Form */}
            <div className="relative w-full max-w-md">
                <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-2xl shadow-2xl border border-slate-800/50 p-8">
                    {/* Logo */}
                    <div className="flex justify-center -mt-4 -mb-4">
                        <img 
                            src={snipeLogo} 
                            alt="Snipe Logo" 
                            className="h-32 w-auto"
                        />
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                        {/* Password Input */}
                        <div>
                            <label 
                                htmlFor="password" 
                                className="block text-sm font-medium text-slate-300 mb-2 text-center"
                            >
                                Enter dev password:
                            </label>
                            <input
                                type="password"
                                id="password"
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    setError('');
                                }}
                                className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition duration-200"
                                placeholder="Enter password"
                                disabled={isLoading}
                                autoComplete="off"
                                autoFocus
                            />
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
                                <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="text-sm text-red-300">{error}</p>
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:scale-[1.02] transform"
                        >
                            {isLoading ? (
                                <>
                                    <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                    <span>Verifying...</span>
                                </>
                            ) : (
                                <>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                    </svg>
                                    <span>Enter Website</span>
                                </>
                            )}
                        </button>
                    </form>

                    {/* Footer Note */}
                    <div className="mt-6 pt-6 border-t border-slate-800/50">
                        <p className="text-xs text-slate-500 text-center">
                            Development environment - Password required for access
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PasswordProtection;

